import { useEffect, useState } from "react";
import api from "@/lib/api";

// Simple in-memory cache — one fetch per page_slug per session.
const cache = {};
const inflight = {};

/**
 * useContent — resolves editable copy for a given page.
 *
 * Usage:
 *   const { t } = useContent("home");
 *   <h1>{t("hero.title", "A Living Sanctuary. A Practical School.")}</h1>
 *
 * If the admin has set a value for the block via /admin/content, it is used.
 * Otherwise the hardcoded `fallback` (passed as the second argument) is used.
 * That means the site NEVER breaks if the DB is empty or unreachable.
 */
export function useContent(pageSlug) {
  const [blocks, setBlocks] = useState(cache[pageSlug] || null);

  useEffect(() => {
    if (cache[pageSlug]) { setBlocks(cache[pageSlug]); return; }
    if (inflight[pageSlug]) {
      inflight[pageSlug].then((d) => setBlocks(d)).catch(() => setBlocks({}));
      return;
    }
    const p = api.get(`/content/pages/${pageSlug}`)
      .then(({ data }) => { cache[pageSlug] = data || {}; return cache[pageSlug]; })
      .catch(() => { cache[pageSlug] = {}; return cache[pageSlug]; })
      .finally(() => { delete inflight[pageSlug]; });
    inflight[pageSlug] = p;
    p.then((d) => setBlocks(d));
  }, [pageSlug]);

  const t = (blockKey, fallback = "") => {
    if (!blocks) return fallback;
    const v = blocks[blockKey];
    return (v && String(v).trim().length > 0) ? v : fallback;
  };

  return { t, ready: blocks !== null };
}

// Allow admins editing content to clear cache and pull fresh values.
export function invalidateContentCache(pageSlug) {
  if (pageSlug) delete cache[pageSlug];
  else Object.keys(cache).forEach((k) => delete cache[k]);
}
